import { EditOutlined, EyeOutlined, InboxOutlined, PlusOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload';
import {
  App,
  Button,
  Form,
  Input,
  Modal,
  Space,
  Table,
  Tag,
  Typography,
  Upload,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { REPORT_IMAGE_MAX_COUNT, REPORT_IMAGE_MAX_SIZE_MB } from '@/constants/report';
import {
  PROJECT_REPORT_STATUS_LABELS,
  projectReportStatusTagColor,
} from '@/constants/project-report';
import { ProjectReportDetailDrawer } from '@/pages/admin/project-reports/ProjectReportDetailDrawer';
import { ApiError } from '@/services/api/client';
import * as projectReportsApi from '@/services/api/project-reports';
import * as uploadsApi from '@/services/api/uploads';
import type { UploadedReportImage } from '@/types/report';
import type { ProjectReportRecord } from '@/types/project-report';
import { formatProjectReportDate } from '@/utils/format-date';

interface ProjectProgressTimelineSectionProps {
  projectId: string;
  projectName: string;
  editable: boolean;
  onChanged?: () => void;
}

interface ProjectProgressFormValues {
  progressContent: string;
  problemContent?: string;
  nextPlanContent?: string;
}

interface WorkWeek {
  startKey: string;
  endKey: string;
  todayKey: string;
  label: string;
}

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

function getCurrentWorkWeek(): WorkWeek {
  const current = dayjs().startOf('day');
  const weekday = current.day();
  const offsetToMonday = weekday === 0 ? -6 : 1 - weekday;
  const start = current.add(offsetToMonday, 'day');
  const end = start.add(4, 'day');
  const startKey = start.format('YYYY-MM-DD');
  const endKey = end.format('YYYY-MM-DD');
  return {
    startKey,
    endKey,
    todayKey: current.format('YYYY-MM-DD'),
    label: `${startKey} 至 ${endKey}`,
  };
}

function renderFullText(text: string | null | undefined) {
  const value = (text || '').trim();
  return (
    <Typography.Paragraph
      style={{ marginBottom: 0, overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}
    >
      {value || '—'}
    </Typography.Paragraph>
  );
}

export function ProjectProgressTimelineSection({
  projectId,
  projectName,
  editable,
  onChanged,
}: ProjectProgressTimelineSectionProps) {
  const { message } = App.useApp();
  const [form] = Form.useForm<ProjectProgressFormValues>();
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reports, setReports] = useState<ProjectReportRecord[]>([]);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailReportId, setDetailReportId] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingReportId, setEditingReportId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loadingImages, setLoadingImages] = useState(false);
  const [images, setImages] = useState<UploadedReportImage[]>([]);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const imageLoadVersionRef = useRef(0);
  const pendingUploadCountRef = useRef(0);
  const previewUrlsRef = useRef(new Set<string>());
  const currentWeek = useMemo(() => getCurrentWorkWeek(), []);

  const currentWeekReport = useMemo(
    () =>
      reports.find(
        (report) =>
          report.reportDate >= currentWeek.startKey && report.reportDate <= currentWeek.endKey,
      ) ?? null,
    [currentWeek.endKey, currentWeek.startKey, reports],
  );

  const loadReports = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const result = await projectReportsApi.fetchProjectReportsByProject(projectId, {
        page: 1,
        pageSize: 100,
        reportPeriod: 'WEEKLY',
      });
      setReports(result.items);
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '加载项目进展失败';
      message.error(text);
      setReports([]);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [message, projectId]);

  useEffect(() => {
    void loadReports();
  }, [loadReports]);

  const revokePreviewUrls = () => {
    previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    previewUrlsRef.current.clear();
  };

  const clearEditor = () => {
    imageLoadVersionRef.current += 1;
    revokePreviewUrls();
    setEditorOpen(false);
    setEditingReportId(null);
    setLoadingImages(false);
    setImages([]);
    setFileList([]);
    form.resetFields();
  };

  useEffect(
    () => () => {
      previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      previewUrlsRef.current.clear();
    },
    [],
  );

  const openEditor = async () => {
    const report = currentWeekReport;
    const loadVersion = imageLoadVersionRef.current + 1;
    imageLoadVersionRef.current = loadVersion;
    revokePreviewUrls();
    setEditingReportId(report?.id ?? null);
    setImages([]);
    setFileList([]);
    form.setFieldsValue({
      progressContent: report?.progressContent ?? '',
      problemContent: report?.problemContent ?? '',
      nextPlanContent: report?.nextPlanContent ?? '',
    });
    setEditorOpen(true);

    const existingImages = report?.images ?? [];
    if (existingImages.length === 0) {
      return;
    }

    setLoadingImages(true);
    const loaded: UploadedReportImage[] = await Promise.all(
      existingImages.map(async (image) => {
        try {
          const previewUrl = await uploadsApi.fetchImageContentUrl(image.id);
          return { ...image, previewUrl } satisfies UploadedReportImage;
        } catch {
          return { ...image } satisfies UploadedReportImage;
        }
      }),
    );

    if (imageLoadVersionRef.current !== loadVersion) {
      loaded.forEach((image) => {
        if (image.previewUrl) URL.revokeObjectURL(image.previewUrl);
      });
      return;
    }

    loaded.forEach((image) => {
      if (image.previewUrl) previewUrlsRef.current.add(image.previewUrl);
    });
    setImages(loaded);
    setFileList(
      loaded.map((image) => ({
        uid: image.id,
        name: image.originalName,
        status: 'done',
        url: image.previewUrl,
      })),
    );
    setLoadingImages(false);
  };

  const handleUpload = async (file: File) => {
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      message.error('仅支持 JPEG、PNG、WebP 图片');
      return Upload.LIST_IGNORE;
    }
    if (file.size > REPORT_IMAGE_MAX_SIZE_MB * 1024 * 1024) {
      message.error(`单张图片不能超过 ${REPORT_IMAGE_MAX_SIZE_MB}MB`);
      return Upload.LIST_IGNORE;
    }
    if (images.length + pendingUploadCountRef.current >= REPORT_IMAGE_MAX_COUNT) {
      message.error(`最多上传 ${REPORT_IMAGE_MAX_COUNT} 张图片`);
      return Upload.LIST_IGNORE;
    }

    pendingUploadCountRef.current += 1;
    setUploading(true);
    try {
      const uploaded = await uploadsApi.uploadReportImage(file);
      if (uploaded.previewUrl) previewUrlsRef.current.add(uploaded.previewUrl);
      setImages((current) => [...current, uploaded]);
      setFileList((current) => [
        ...current,
        {
          uid: uploaded.id,
          name: uploaded.originalName,
          status: 'done',
          url: uploaded.previewUrl,
        },
      ]);
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '图片上传失败';
      message.error(text);
      return Upload.LIST_IGNORE;
    } finally {
      pendingUploadCountRef.current -= 1;
      setUploading(pendingUploadCountRef.current > 0);
    }

    return false;
  };

  const handleRemoveImage = (file: UploadFile) => {
    const removed = images.find((image) => image.id === file.uid);
    if (removed?.previewUrl) {
      URL.revokeObjectURL(removed.previewUrl);
      previewUrlsRef.current.delete(removed.previewUrl);
    }
    setImages((current) => current.filter((image) => image.id !== file.uid));
    setFileList((current) => current.filter((item) => item.uid !== file.uid));
    return true;
  };

  const handleSave = async (values: ProjectProgressFormValues) => {
    if (uploading || loadingImages) {
      message.warning('图片正在处理中，请稍候');
      return;
    }
    const progressContent = values.progressContent.trim();
    const problemContent = values.problemContent?.trim() ?? '';
    const nextPlanContent = values.nextPlanContent?.trim() ?? '';

    setSaving(true);
    try {
      if (editingReportId) {
        await projectReportsApi.updateProjectReport(editingReportId, {
          progressContent,
          problemContent: problemContent || null,
          nextPlanContent: nextPlanContent || null,
          imageIds: images.map((image) => image.id),
        });
      } else {
        await projectReportsApi.createProjectReport({
          projectId,
          reportPeriod: 'WEEKLY',
          reportDate: currentWeek.todayKey,
          progressPercent: 0,
          progressContent,
          problemContent: problemContent || undefined,
          nextPlanContent: nextPlanContent || undefined,
          imageIds: images.map((image) => image.id),
        });
      }

      message.success(editingReportId ? '本周项目进展已更新' : '本周项目进展已提交');
      clearEditor();
      await loadReports();
      onChanged?.();
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        message.warning('本周项目进展已存在，列表已刷新，请重新打开修改');
        clearEditor();
        await loadReports();
      } else {
        const text = error instanceof ApiError ? error.message : '保存项目进展失败';
        message.error(text);
      }
    } finally {
      setSaving(false);
    }
  };

  const columns: ColumnsType<ProjectReportRecord> = [
    {
      title: '填报日期',
      dataIndex: 'reportDate',
      width: 120,
      render: (_: string, record) =>
        formatProjectReportDate(record.reportDate, record.createdAt, record.reportPeriod),
    },
    {
      title: '填报人',
      dataIndex: ['createdBy', 'name'],
      width: 100,
    },
    {
      title: '本周进展',
      dataIndex: 'progressContent',
      render: (value: string) => renderFullText(value),
    },
    {
      title: '存在问题',
      dataIndex: 'problemContent',
      width: 220,
      render: (value: string | null) => renderFullText(value),
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (status: ProjectReportRecord['status']) => (
        <Tag color={projectReportStatusTagColor(status)}>
          {PROJECT_REPORT_STATUS_LABELS[status]}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 90,
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => {
            setDetailReportId(record.id);
            setDetailOpen(true);
          }}
        >
          查看
        </Button>
      ),
    },
  ];

  return (
    <>
      <div
        style={{
          alignItems: 'flex-start',
          display: 'flex',
          gap: 16,
          justifyContent: 'space-between',
          marginBottom: 12,
        }}
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
          项目负责人和管理员填写的周进展使用同一份记录，最新一条同步展示在项目台账中。
        </Typography.Paragraph>
        {editable ? (
          <Button
            type="primary"
            icon={currentWeekReport ? <EditOutlined /> : <PlusOutlined />}
            disabled={loading || loadFailed}
            onClick={() => void openEditor()}
          >
            {currentWeekReport ? '修改本周进展' : '填写本周进展'}
          </Button>
        ) : (
          <Typography.Text type="secondary">归档项目不可修改</Typography.Text>
        )}
      </div>

      <Table
        rowKey="id"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={reports}
        pagination={{ pageSize: 10, hideOnSinglePage: true, showSizeChanger: false }}
        locale={{ emptyText: '暂无项目周报' }}
        scroll={{ x: 900 }}
      />

      <Modal
        title={editingReportId ? '修改本周项目进展' : '填写本周项目进展'}
        open={editorOpen}
        confirmLoading={saving}
        okButtonProps={{ disabled: uploading || loadingImages }}
        okText={editingReportId ? '保存修改' : '提交进展'}
        cancelText="取消"
        onCancel={() => {
          if (!saving && !uploading) clearEditor();
        }}
        onOk={() => form.submit()}
        width={720}
        destroyOnClose
      >
        <Space direction="vertical" size={2} style={{ marginBottom: 16 }}>
          <Typography.Text strong>{projectName}</Typography.Text>
          <Typography.Text type="secondary">本周周期：{currentWeek.label}</Typography.Text>
          <Typography.Text type="secondary">填报日期：{currentWeek.todayKey}</Typography.Text>
        </Space>
        <Form form={form} layout="vertical" onFinish={(values) => void handleSave(values)}>
          <Form.Item
            name="progressContent"
            label="本周进展"
            rules={[
              { required: true, whitespace: true, message: '请填写本周进展' },
              { max: 10000, message: '本周进展不能超过 10000 字' },
            ]}
          >
            <Input.TextArea
              rows={5}
              maxLength={10000}
              showCount
              placeholder="填写本周完成事项和关键节点进展"
            />
          </Form.Item>
          <Form.Item name="problemContent" label="存在问题" rules={[{ max: 10000 }]}>
            <Input.TextArea
              rows={3}
              maxLength={10000}
              showCount
              placeholder="没有可不填"
            />
          </Form.Item>
          <Form.Item name="nextPlanContent" label="下周计划" rules={[{ max: 10000 }]}>
            <Input.TextArea
              rows={3}
              maxLength={10000}
              showCount
              placeholder="填写下一周计划，没有可不填"
            />
          </Form.Item>
          <Form.Item label={`进展图片（最多 ${REPORT_IMAGE_MAX_COUNT} 张）`}>
            <Upload.Dragger
              multiple
              maxCount={REPORT_IMAGE_MAX_COUNT}
              listType="picture"
              fileList={fileList}
              beforeUpload={(file) => handleUpload(file as File)}
              onRemove={handleRemoveImage}
              disabled={saving || uploading || loadingImages}
              accept={ALLOWED_IMAGE_TYPES.join(',')}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                {loadingImages ? '正在读取已有图片' : '点击或拖拽上传进展图片'}
              </p>
              <p className="ant-upload-hint">
                支持 JPEG/PNG/WebP，单张不超过 {REPORT_IMAGE_MAX_SIZE_MB}MB
              </p>
            </Upload.Dragger>
          </Form.Item>
        </Form>
      </Modal>

      <ProjectReportDetailDrawer
        open={detailOpen}
        reportId={detailReportId}
        onClose={() => {
          setDetailOpen(false);
          setDetailReportId(null);
        }}
        onChanged={() => {
          void loadReports();
          onChanged?.();
        }}
      />
    </>
  );
}
